import { type Browser, detect } from "detect-browser";
import { Link } from "react-router-dom";

import { selMessage } from "../messages/message.js";
import { SelMessage } from "../messages/SelMessage.js";
import { notify } from "../modules/notifications/notify.js";

export enum BrowserSupportLevel {
  UNSUPPORTED = 0,
  PARTLY = 1,
  SUPPORTED = 2,
}

interface IBrowserVersionNumbers {
  major: number;
  minor: number;
  patch: number;
}

export interface IBrowserCompatibilityRule {
  name: string;
  partlyMajor: number;
  // By not setting a `fullMajor` we emit a warning due to partial support
  fullMajor?: number;
  url: string;
}

export interface IClientSetup {
  clientBrowser?: string;
  clientOS: string;
}

function getClientOS(): string {
  let OSName = window.navigator.platform;

  if (window.navigator.userAgent.includes("Windows")) {
    /* Windows window.navigator.platform returns Win32 all the time */
    OSName = "Windows";
    // Please note: Windows 11 will not be visible inside the user agent:
    // https://groups.google.com/a/chromium.org/g/blink-dev/c/-2JIRNMWJ7s/m/yHe4tQNLCgAJ
    if (window.navigator.userAgent.includes("Windows NT 10.0")) OSName = "Windows 10";
    if (window.navigator.userAgent.includes("Windows NT 6.2")) OSName = "Windows 8";
    if (window.navigator.userAgent.includes("Windows NT 6.1")) OSName = "Windows 7";
    if (window.navigator.userAgent.includes("Windows NT 6.0")) OSName = "Windows Vista";
    if (window.navigator.userAgent.includes("Windows NT 5.1")) OSName = "Windows XP";
    if (window.navigator.userAgent.includes("Windows NT 5.0")) OSName = "Windows 2000";
    const bits = isWin64() ? 64 : 32;
    return `${OSName} (${bits}-bit)`;
  }
  return OSName;
}

function isWin64(): boolean {
  return window.navigator.userAgent.includes("Win64") || window.navigator.userAgent.includes("WOW64");
}

export function getClientSetup(): IClientSetup {
  return {
    clientBrowser: browser ? browser.name + " " + browser.version : undefined,
    clientOS: getClientOS(),
  };
}

function splitBrowserVersion(version: string): IBrowserVersionNumbers {
  const part = version.split(".");

  try {
    return {
      major: Number.parseInt(part[0], 10),
      minor: Number.parseInt(part[1], 10),
      patch: Number.parseInt(part[2], 10),
    };
  } catch {
    return {
      major: 0,
      minor: 0,
      patch: 0,
    };
  }
}

function checkBrowserCompatibility(rule: IBrowserCompatibilityRule, version: string): BrowserSupportLevel {
  const { major } = splitBrowserVersion(version);
  if (major < rule.partlyMajor) {
    return BrowserSupportLevel.UNSUPPORTED;
  }
  if (rule.fullMajor === undefined || major < rule.fullMajor) {
    return BrowserSupportLevel.PARTLY;
  }
  return BrowserSupportLevel.SUPPORTED;
}

type SupportedBrowser = Extract<Browser, "chrome" | "edge-chromium" | "firefox" | "safari">;
const browser = detect();
const browserCompatibilityMap: Record<SupportedBrowser, IBrowserCompatibilityRule> = {
  chrome: {
    partlyMajor: 119, // no partial support anymore
    fullMajor: 119, // following react-pdf support
    name: "Google Chrome",
    url: "https://www.google.com/chrome/",
  },
  "edge-chromium": {
    partlyMajor: 119, // no partial support anymore
    fullMajor: 119, // following react-pdf support
    name: "New Microsoft Edge",
    url: "https://www.microsoft.com/edge",
  },
  firefox: {
    partlyMajor: 128, // no partial support anymore
    fullMajor: 128, // following react-pdf support
    name: "Mozilla Firefox",
    url: "https://www.mozilla.org/firefox/",
  },
  safari: {
    partlyMajor: 17.4, // no partial support anymore
    // `undefined` because we do not officially support Safari.
    // We bring up a warning but we don't prevent its use.
    fullMajor: undefined,
    name: "Safari",
    url: "https://www.apple.com/safari/",
  },
};

export function supportedBrowsersList(showPartlySupported = true) {
  const list = Object.values(browserCompatibilityMap)
    .filter((rule) => !!rule.name && (showPartlySupported || !!rule.fullMajor))
    .map((rule, index: number) => {
      const name = rule.url ? <a href={rule.url}>{rule.name}</a> : rule.name;
      return (
        <li key={`suggested-browser-${index}`}>
          {" "}
          {name}, {!rule.fullMajor && <b> limited support, </b>}
          {"version " + (rule.fullMajor || rule.partlyMajor) + " or newer"}
        </li>
      );
    });

  return <ul style={{ textAlign: "left" }}>{list}</ul>;
}

export function browserWarning() {
  return () => {
    // display a browser warning in 2 sec from now.
    setTimeout(() => {
      notify.warning(
        {
          title: selMessage({ id: "browserCompatibility.limitedSupport.warningTitle" }),
          children: SelMessage({
            id: "browserCompatibility.limitedSupport.warningMessage",
            values: { link: (chunk: string) => <Link to="/limitedBrowserSupport">{chunk}</Link> },
          }),
        },
        {
          autoClose: false, // stay until user closes it
        },
      );
    }, 1000);
  };
}

export const browserSupportLevel = (() => {
  if (!browser || !browser.name || !browser.version) {
    return BrowserSupportLevel.UNSUPPORTED;
  }
  const rule = browserCompatibilityMap[browser.name as SupportedBrowser];
  if (!rule) {
    return BrowserSupportLevel.UNSUPPORTED;
  }
  return checkBrowserCompatibility(rule, browser.version);
})();
